import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Button,
  ControlledInput,
  ControlledPasswordInput,
  useZodHookForm,
  VerticalSpacing,
  z,
} from '@willow/shared-web';
import { Form } from '@willow/shared-web/bootstrap';
import { PORTAL_LOGIN_PATH, PortalLoginConfigEnabled } from '@willow/types-iso';

import { authEmailSchema, authNewPasswordSchema } from '../../auth/authFormSchema';
import { AuthLayout } from '../../auth/AuthLayout';
import { FORGOT_PASSWORD_PATH } from '../../auth/authRoutes';
import { hardNavigate } from '../../auth/hardNavigate';
import { acceptInvite, resetPassword } from '../../auth/portalAuthApi';
import { Alert } from '../../components/alert/Alert';

interface TSetPasswordForm {
  email: string;
  newPassword: string;
  confirmPassword: string;
}

const passwordFields = {
  newPassword: authNewPasswordSchema,
  confirmPassword: z.string(),
};

const passwordsMatch = (values: TSetPasswordForm) => values.newPassword === values.confirmPassword;
const passwordsMatchError = { error: 'The passwords do not match', path: ['confirmPassword'] };

// The invite endpoint needs the email, the reset endpoint does not, so only the invite flow
// validates it.
const InviteFormShape: z.ZodType<TSetPasswordForm> = z
  .object({ email: authEmailSchema, ...passwordFields })
  .refine(passwordsMatch, passwordsMatchError);

const ResetFormShape: z.ZodType<TSetPasswordForm> = z
  .object({ email: z.string(), ...passwordFields })
  .refine(passwordsMatch, passwordsMatchError);

interface Props {
  config: PortalLoginConfigEnabled;
}

export const SetPasswordPage = ({ config }: Props) => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const isReset = params.get('flow') === 'reset';
  const invitedEmail = params.get('email')?.trim() ?? '';

  // The token is a single-use credential, so it is read once from the initial location and then
  // stripped from the address bar below. Anything that records a URL keeps a copy otherwise:
  // browser history, Sentry events raised from this page, screenshots.
  const [token] = useState(() => new URLSearchParams(search).get('token') ?? '');
  const [invalidLink, setInvalidLink] = useState(() => {
    const initial = new URLSearchParams(search);
    return !initial.get('token') || initial.get('error') === 'INVALID_TOKEN';
  });
  const [resetComplete, setResetComplete] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  useEffect(() => {
    const next = new URLSearchParams(search);
    if (!next.has('token')) return;
    next.delete('token');
    const query = next.toString();
    history.replace({ pathname, search: query ? `?${query}` : '' });
  }, [history, pathname, search]);

  const {
    control,
    formState: { isSubmitting },
    getValues,
    handleSubmit,
    setError,
    setValue,
  } = useZodHookForm(isReset ? ResetFormShape : InviteFormShape, {
    defaultValues: { email: invitedEmail, newPassword: '', confirmPassword: '' },
  });

  const title = 'Set Your Password';

  const onSubmit = handleSubmit(async (data) => {
    setSubmitFailed(false);
    const result = isReset
      ? await resetPassword({ token, newPassword: data.newPassword })
      : await acceptInvite({ token, email: data.email, newPassword: data.newPassword });

    if (result.ok) {
      if (isReset) {
        setResetComplete(true);
      } else {
        // accept-invite sets the session cookie, so the borrower lands in the portal signed in.
        hardNavigate('/');
      }
      return;
    }

    // USER_NOT_FOUND is only reachable behind a valid token, so it reads as a dead link rather
    // than as copy of its own.
    if (result.code === 'INVALID_TOKEN' || result.code === 'USER_NOT_FOUND') {
      setInvalidLink(true);
      return;
    }

    if (result.code.startsWith('PASSWORD_')) {
      setValue('newPassword', '');
      setValue('confirmPassword', '');
      // portalAuthApi only forwards a server message for the PASSWORD_* codes, and it can still be
      // absent. Without a fallback the fields are cleared and marked invalid with nothing said.
      setError('newPassword', { message: result.message ?? 'Choose a different password' });
      return;
    }

    setSubmitFailed(true);
  });

  if (invalidLink) {
    const email = isReset ? '' : getValues('email').trim();
    const requestNewLinkTo = email
      ? `${FORGOT_PASSWORD_PATH}?email=${encodeURIComponent(email)}`
      : FORGOT_PASSWORD_PATH;
    return (
      <AuthLayout portal={config.portal} documentTitle="Set Password" title="Access your Payment Portal">
        <VerticalSpacing spacing={2}>
          <div className="auth-layout__aligned" role="alert" aria-live="assertive">
            <Alert
              level="WARNING"
              title="This link is invalid or has expired."
              subtitle={
                <>
                  <div>
                    Links expire after a while for your security. Request a new one and we will email it to you.
                  </div>
                  {!isReset && (
                    <div>If you typed an email address, check it is the one your invitation was sent to.</div>
                  )}
                </>
              }
            />
          </div>
          <Button
            size="lg"
            className="w-100"
            innerClassName="justify-content-center"
            onClick={() => history.push(requestNewLinkTo)}
          >
            Request a new link
          </Button>
        </VerticalSpacing>
      </AuthLayout>
    );
  }

  if (resetComplete) {
    return (
      <AuthLayout
        portal={config.portal}
        documentTitle="Password Set"
        title="Password Set"
        description={
          <>Your password has been created successfully! For your security, any other devices were signed out.</>
        }
      >
        <Button
          size="lg"
          className="w-100"
          innerClassName="justify-content-center"
          onClick={() => history.push(PORTAL_LOGIN_PATH)}
        >
          Back to login
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      portal={config.portal}
      documentTitle="Set Password"
      title={title}
      description="Enter and confirm your password below"
    >
      <div className="auth-layout__aligned" role="alert" aria-live="assertive">
        {submitFailed && (
          <Alert
            level="ERROR"
            title="We could not save your password right now."
            subtitle="Please try again in a moment."
          />
        )}
      </div>

      <Form onSubmit={onSubmit}>
        <VerticalSpacing spacing={2}>
          {!isReset && (
            <Form.Group controlId="email">
              <ControlledInput
                control={control}
                fieldName="email"
                label="Email"
                hint="Use the email address your invitation was sent to"
                inputType="email"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
              />
            </Form.Group>
          )}

          <Form.Group controlId="newPassword">
            <ControlledPasswordInput
              control={control}
              fieldName="newPassword"
              label="New password"
              hint="At least 15 characters. A short sentence you will remember works well."
              autoComplete="new-password"
              autoFocus={isReset}
              iconOnlyToggle
            />
          </Form.Group>

          <Form.Group controlId="confirmPassword">
            <ControlledPasswordInput
              control={control}
              fieldName="confirmPassword"
              label="Confirm new password"
              autoComplete="new-password"
              iconOnlyToggle
            />
          </Form.Group>

          <Button
            type="submit"
            size="lg"
            className="w-100"
            innerClassName="justify-content-center"
            loading={isSubmitting}
          >
            Confirm Password
          </Button>
        </VerticalSpacing>
      </Form>
    </AuthLayout>
  );
};
